import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Tab, Tabs, TextField } from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface HorseAgePickerProps {
    birthday: Dayjs | null;
    setBirthday: (value: Dayjs | null) => void;
    age: string | number;
    setAge: (value: string) => void;
    is_south_hemisphere: boolean;
    activeTab: number,
    setActiveTab: (value: number) => void;
    setIsSouthHemisphere: (value: boolean) => void;
    errors: {
        birthday?: boolean;
        horse_age?: boolean;
    };
}

export default function HorseAgePicker({
    birthday,
    setBirthday,
    age,
    setAge,
    is_south_hemisphere,
    setIsSouthHemisphere,
    errors,
    activeTab,
    setActiveTab
}: HorseAgePickerProps) {
    const { t } = useTranslation()
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Tab Navigation */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 'bold' }
                }}
            >
                <Tab label={t('ecg:requestAnalysis:ikAge', "Je connais l'âge")} />
                <Tab label={t('ecg:requestAnalysis:ikBirthday', 'Je connais la date de naissance')} />
            </Tabs>

            {/* Tab 1: Date Picker (Year / Month) */}
            {activeTab === 1 && (
                <DatePicker<Dayjs>
                    value={birthday}
                    views={['year', 'month']}
                    label={t('addHorse:birthdate')}
                    format="YYYY / MM"
                    onChange={(value) => setBirthday(value ? dayjs(value) : null)}
                    slotProps={{
                        textField: {
                            required: true,
                            error: !!errors.birthday,
                            fullWidth: true,
                        },
                    }}
                />
            )}

            {/* Tab 2: Age Input + Hemisphere Toggle */}
            {activeTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        label={t('age')}
                        type="number"
                        value={age}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAge(e.target.value)}
                        required
                        error={!!errors.horse_age}
                        fullWidth
                        inputProps={{ min: 0, max: 50 }}
                    />

                    <FormControl component="fieldset" fullWidth>
                        <FormLabel
                            component="legend"
                            sx={{
                                typography: 'body2',
                                color: 'text.secondary',
                                mb: 1,
                                fontWeight: 500,
                                '&.Mui-focused': { color: 'text.secondary' } // Keeps label color consistent
                            }}
                        >
                            {t("ecg:requestAnalysis:hemisphere")}
                        </FormLabel>
                        <RadioGroup
                            row
                            name="hemisphere-selector"
                            value={is_south_hemisphere ? 'south' : 'north'}
                            onChange={(e) => setIsSouthHemisphere(e.target.value === 'south')}
                            sx={{ gap: 2 }} // Adds nice breathing room between the two choices
                        >
                            <FormControlLabel
                                value="north"
                                control={<Radio size="small" color="primary" />}
                                label={t('ecg:requestAnalysis:north')}
                            />
                            <FormControlLabel
                                value="south"
                                control={<Radio size="small" color="primary" />}
                                label={t('ecg:requestAnalysis:south')}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            )}
        </Box>
    );
}